// @flow
import TaskGroupCollection from "../taskgroup/TaskGroupCollection";
import ResourceModel from "../base/ResourceModel";
import TaskGroupModel from "../taskgroup/TaskGroupModel";
import ActionCollection from "../actions/ActionCollection";

import type { ModularUIModel } from "../types";
import type { ModularUIResponse } from "../../modularui";
import type LinkModel from "../links/LinkModel";
import type LinkCollection from "../links/LinkCollection";

/**
 * Describes a TabModel
 */
export default class TabModel extends ResourceModel {
  _actionCollection: ActionCollection;
  _taskGroupCollection: TaskGroupCollection;

  /**
   */
  constructor(modularuiResponse: ModularUIResponse) {
    super(modularuiResponse);

    this.createTaskGroupCollection();

    this._actionCollection = new ActionCollection(
      this.data.actions,
      this.contributions.actions,
      this.modelOptions,
    );
  }

  /**
   */
  get type(): string {
    return "Tab";
  }

  /**
   */
  static get modelName(): string {
    return "TabModel";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    return (
      data.contributions.resourcetype &&
      (data.contributions.resourcetype.endsWith("Tab") ||
        data.contributions.resourcetype.endsWith("KnowledgeBank"))
    );
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    return this.links.getLinksByGroup("taskgroup", "actions").all;
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>) {
    const taskGroups = [];

    for (let i = 0; i < models.length; i++) {
      if (models[i] instanceof TaskGroupModel) {
        taskGroups.push(models[i]);
      }
    }

    this.taskGroupCollection.add(taskGroups);
  }

  /**
   */
  get searchLink(): ?LinkModel {
    return this.links.getLinksByGroup("search").first;
  }

  /**
   */
  createTaskGroupCollection() {
    this.taskGroupCollection = this.data.taskgroups
      ? this.data.taskgroups
          .map((taskgroup) => {
            const taskgroupContributions = this.contributions.taskgroups.find(
              (taskgroupContribution) =>
                taskgroupContribution.name === taskgroup.name,
            );

            if (taskgroupContributions) {
              return TaskGroupModel.create(
                taskgroup.name,
                taskgroup,
                taskgroupContributions,
                this.modelOptions,
              );
            }

            return null;
          })
          .filter((taskgroup) => taskgroup != null)
      : [];
  }

  /**
   * Getting the taskgrouppanels on the tab
   */
  get taskGroupCollection(): TaskGroupCollection {
    return this._taskGroupCollection;
  }

  /**
   * Setting the taskgroup panel collection
   */
  set taskGroupCollection(taskgroups: Array<TaskGroupModel>) {
    this._taskGroupCollection = new TaskGroupCollection(taskgroups);
  }

  /**
   * Getting the label of the tab
   */
  get label(): string {
    return this.getContribution("label", "");
  }

  /**
   * Getting the component links on the tab
   */
  get components(): LinkCollection {
    return this.links.getLinksByGroup("component", "search");
  }

  /**
   */
  get actionCollection(): ActionCollection {
    return this._actionCollection;
  }

  /**
   * Has component links
   */
  hasComponents(): boolean {
    return this.components.size > 0;
  }

  /**
   * Has taskgroups
   */
  hasTaskGroups(): boolean {
    return this.taskGroupCollection && this.taskGroupCollection?.hasItems;
  }

  /**
   */
  hasActions(): boolean {
    return this.actionCollection && this.actionCollection.hasItems;
  }
}
